import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AuthGate } from "@/components/access/AuthGate";
import { ClientRouteCanonicalizer } from "@/components/shell2/ClientRouteCanonicalizer";
import { PlatformShell } from "@/components/shell2/PlatformShell";
import ClientsPage from "@/pages/ClientsPage";
import LoginPage from "@/pages/LoginPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { adminRoutes } from "@/routes/adminRoutes";
import { devRoutes } from "@/routes/devRoutes";
import { platformRoutes } from "@/routes/platformRoutes";
import { studioRoutes } from "@/routes/studioRoutes";
import { workspaceRoutes } from "@/routes/workspaceRoutes";

const PublicBriefPage = lazy(
  () => import("@/features/studio/briefs/PublicBriefPage"),
);
const ReviewPage = lazy(
  () => import("@/features/review-approval/pages/ReviewPage"),
);

// Loading component (shared; referenced by route modules)
export const PageLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="text-gray-600">Loading...</div>
  </div>
);

export default function AppRoutes() {
  return (
    <>
      {/*
        Canonicalize ?client= for EVERY route, before any shell mounts. Mounted
        once here (not per-shell) so bare routes without a PlatformShell /
        WorkspaceShell2 wrapper — e.g. /clients, /admin/* — are covered too.
        It renders null and is a no-op on already-canonical URLs.
      */}
      <ClientRouteCanonicalizer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        {/* Request Access retired 2026-09-29 (beta_request_submit_anon_revoked).
            Kept as a redirect so old links land on sign-in, not a 404. */}
        <Route
          path="/request-access"
          element={<Navigate to="/login" replace />}
        />
        <Route
          path="/briefs/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicBriefPage />
            </Suspense>
          }
        />
        <Route
          path="/review/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <ReviewPage />
            </Suspense>
          }
        />

        {/* Platform-level routes (actions, home, help, cockpit, oauth) */}
        {platformRoutes()}

        {/* Workspace routes (clients list, /w/:clientSlug/*, legacy redirects) */}
        {workspaceRoutes()}

        {/* Studio routes (internal Track15 tooling, allowlist-gated) */}
        {studioRoutes()}

        {/* Admin routes (/admin/*) */}
        {adminRoutes()}

        {/* Dev / billing / event-planner routes (feature-flag gated) */}
        {devRoutes()}

        {/* Root - client doorway (Home is a doorway, not a judge).
            Rendered inside PlatformShell like every other platform page, so
            the sidebar stays: it used to be the one shell-less platform page,
            stranding anyone who clicked "Clients" (navigation_audit F2). */}
        <Route
          index
          element={
            <AuthGate>
              <PlatformShell>
                <ClientsPage />
              </PlatformShell>
            </AuthGate>
          }
        />

        {/* Catch-all route for 404s */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
